import {ReactNode} from 'react';
import {useRevealOnView} from './useRevealOnView';
import styles from './Step9Viz.module.css';

export type FamilyTreeItemIcon = 'pill' | 'syringe' | 'drops';
export type FamilyTreeItem = {name: ReactNode; indication: string; iconKind?: FamilyTreeItemIcon};

const PillGlyph = ({size = 18, color = 'currentColor'}: {size?: number; color?: string}) => (
  <svg width={size} height={size * 0.58} viewBox="0 0 24 14" aria-hidden="true">
    <rect
      x="0.7"
      y="0.7"
      width="22.6"
      height="12.6"
      rx="6.3"
      fill="none"
      stroke={color}
      strokeWidth="1.5"
      strokeOpacity="0.85"
    />
    <line x1="12" y1="1" x2="12" y2="13" stroke={color} strokeWidth="1.5" strokeOpacity="0.85" />
    <rect x="0.7" y="0.7" width="11" height="12.6" rx="6.3" fill={color} fillOpacity="0.18" />
  </svg>
);

const SyringeGlyph = ({color = 'currentColor', size = 20}: {color?: string; size?: number}) => (
  <svg width={size} height={size} viewBox="0 0 20 20" aria-hidden="true">
    <path
      d="M2.22222 8.33333H1.66667C1.35984 8.33333 1.11111 8.58206 1.11111 8.88889V11.1111C1.11111 11.4179 1.35984 11.6667 1.66667 11.6667H2.22222C2.52905 11.6667 2.77778 11.4179 2.77778 11.1111V8.88889C2.77778 8.58206 2.52905 8.33333 2.22222 8.33333Z"
      fill={color}
    />
    <path d="M4.44444 9.44444H2.77778V10.5556H4.44444V9.44444Z" fill={color} />
    <path
      d="M12.6667 7.22222H5.11111C4.74292 7.22222 4.44444 7.5207 4.44444 7.88889V12.1111C4.44444 12.4793 4.74292 12.7778 5.11111 12.7778H12.6667C13.0349 12.7778 13.3333 12.4793 13.3333 12.1111V7.88889C13.3333 7.5207 13.0349 7.22222 12.6667 7.22222Z"
      fill={color}
    />
    <path d="M14.7222 8.88889H13.3333V11.1111H14.7222V8.88889Z" fill={color} />
    <path d="M18.8889 9.72222H14.7222V10.2778H18.8889V9.72222Z" fill={color} />
  </svg>
);

const DropsGlyph = ({color = 'currentColor', size = 16}: {color?: string; size?: number}) => (
  <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden="true">
    <path
      d="M6.22222 2.66667C6.22222 2.66667 2.66667 7.11111 2.66667 9.77778C2.66667 11.7333 4.26667 13.3333 6.22222 13.3333C8.17778 13.3333 9.77778 11.7333 9.77778 9.77778C9.77778 7.11111 6.22222 2.66667 6.22222 2.66667Z"
      fill={color}
    />
    <path
      d="M11.5556 5.33333C11.5556 5.33333 9.77778 8 9.77778 9.55556C9.77778 10.6578 10.5733 11.5556 11.5556 11.5556C12.5378 11.5556 13.3333 10.6578 13.3333 9.55556C13.3333 8 11.5556 5.33333 11.5556 5.33333Z"
      fill={color}
    />
  </svg>
);

const FamilyItemGlyph = ({kind, color}: {kind: FamilyTreeItemIcon; color: string}) => {
  if (kind === 'syringe') return <SyringeGlyph size={20} color={color} />;
  if (kind === 'drops') return <DropsGlyph size={16} color={color} />;
  return <PillGlyph size={20} color={color} />;
};

/** Parent "GLP-1 family" pill, vertical trunk, and rows with L-elbow tree
 *  lines connecting to each medication card. Highlighted row gets a gold
 *  glow + "Your treatment" badge. */
export const FamilyTreeViz = ({
  items,
  highlightedIdx,
  delayMs = 0,
}: {
  items: FamilyTreeItem[];
  highlightedIdx: number;
  delayMs?: number;
}) => {
  const {ref, triggered} = useRevealOnView();
  return (
    <div ref={ref} className={styles.treeRoot}>
      <div
        className={`${styles.parentPill} ${triggered ? styles.parentPillOn : ''}`}
        style={{transitionDelay: `${delayMs}ms`}}
      >
        <PillGlyph size={14} color="var(--webpurple1)" />
        <span className={styles.parentPillText}>GLP-1 family</span>
      </div>

      <div className={styles.rowsWrap}>
        <span
          className={`${styles.trunk} ${triggered ? styles.trunkOn : ''}`}
          style={{transitionDelay: `${delayMs + 200}ms`}}
          aria-hidden="true"
        />
        {items.map((item, i) => {
          const isHl = i === highlightedIdx;
          const rowDelay = delayMs + 700 + i * 240;
          return (
            <div
              key={i}
              className={`${styles.row} ${triggered ? styles.rowOn : ''}`}
              style={{transitionDelay: `${rowDelay}ms`}}
            >
              <span
                className={`${styles.elbow} ${triggered ? styles.elbowOn : ''}`}
                style={{transitionDelay: `${rowDelay - 80}ms`}}
                aria-hidden="true"
              />
              <div className={`${styles.medCard} ${isHl ? styles.medCardHl : styles.medCardDefault}`}>
                <span
                  className={styles.medGlyph}
                  style={{color: isHl ? 'var(--osa-brand-gold-deep)' : 'var(--webpurple1)'}}
                >
                  <FamilyItemGlyph kind={item.iconKind ?? 'pill'} color="currentColor" />
                </span>
                <div className={styles.medText}>
                  <span className={styles.medName}>{item.name}</span>
                  <span className={styles.medIndication}>{item.indication}</span>
                </div>
                {isHl ? <span className={styles.yourTreatmentBadge}>Your treatment</span> : null}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const MECHANISM_STEPS = [
  {label: 'Body weight', widthPct: 100},
  {label: 'Airway pressure', widthPct: 68},
  {label: 'Sleep interruptions', widthPct: 38},
] as const;

const DownArrow = () => (
  <svg width="14" height="22" viewBox="0 0 14 22" aria-hidden="true">
    <path
      d="M7 2 V17 M2.5 13 L7 17.5 L11.5 13"
      fill="none"
      stroke="rgba(12,10,24,0.45)"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** Three stacked bars (Body weight → Airway pressure → Sleep interruptions)
 *  visually communicating the mechanism cascade. Each bar grows in from the
 *  left, arrows fall between rows. */
export const MechanismChainViz = ({delayMs = 0}: {delayMs?: number}) => {
  const {ref, triggered} = useRevealOnView();
  return (
    <div ref={ref} className={styles.mechRoot}>
      {MECHANISM_STEPS.map((step, i) => {
        const stepDelay = delayMs + i * 700;
        return (
          <div key={step.label}>
            <div className={styles.mechStep}>
              <span
                className={`${styles.mechLabel} ${triggered ? styles.mechLabelOn : ''}`}
                style={{transitionDelay: `${stepDelay}ms`}}
              >
                {step.label.toUpperCase()}
              </span>
              <div className={styles.mechBarTrack} style={{width: `${step.widthPct}%`}}>
                <span
                  className={`${styles.mechBarFill} ${triggered ? styles.mechBarFillOn : ''}`}
                  style={{transitionDelay: `${stepDelay + 100}ms`}}
                />
              </div>
            </div>
            {i < MECHANISM_STEPS.length - 1 ? (
              <div
                className={`${styles.arrowWrap} ${triggered ? styles.arrowWrapOn : ''}`}
                style={{transitionDelay: `${stepDelay + 500}ms`}}
              >
                <DownArrow />
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};
